import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Card,
  Button,
  List,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  message,
  Descriptions,
  Popconfirm,
  Tabs,
  Select,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
  MailOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { api } from '../lib/api';

const { Title, Text } = Typography;

interface OrganizationMember {
  id: string;
  userId: string;
  user: {
    id: string;
    email: string;
    name: string | null;
  };
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  joinedAt: string;
}

interface Invitation {
  id: string;
  email: string;
  role: 'OWNER' | 'ADMIN' | 'MEMBER';
  status: string;
  expiresAt: string;
  createdAt: string;
}

interface SeatAssignment {
  id: string;
  userId: string;
  appId: string;
  assignedAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
  };
  app: {
    id: string;
    name: string;
    slug: string;
  };
  subscription?: {
    id: string;
    status: string;
  };
}

interface SeatInfo {
  totalSeats: number;
  usedSeats: number;
  availableSeats: number;
}

export default function OrganizationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [org, setOrg] = useState<any>(null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [seatAssignments, setSeatAssignments] = useState<SeatAssignment[]>([]);
  const [seatInfoByApp, setSeatInfoByApp] = useState<Record<string, SeatInfo>>(
    {}
  );
  const [loading, setLoading] = useState(true);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm();

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      const orgData = await api.organizations.get(id!);
      const role = orgData.organization?.role;
      const [membersData, assignmentsData] = await Promise.all([
        api.organizations.members(id!),
        api.seats.listOrganization(id!),
      ]);
      setOrg(orgData.organization);
      setMembers(membersData.members);
      setSeatAssignments(assignmentsData.assignments || []);
      if (role === 'OWNER' || role === 'ADMIN') {
        try {
          const invitesData = await api.invitations.list(id!);
          setInvitations(invitesData.invitations);
        } catch {
          setInvitations([]);
        }
      } else {
        setInvitations([]);
      }
      await loadSeatInfo(orgData.organization?.subscriptions || []);
    } catch (error: any) {
      message.error('Failed to load organization');
      navigate('/organizations');
    } finally {
      setLoading(false);
    }
  };

  const loadSeatInfo = async (subscriptions: Array<{ app?: { id: string } }>) => {
    if (!id) return;
    const appIds = subscriptions
      .map((sub) => sub.app?.id)
      .filter((appId): appId is string => Boolean(appId));

    const uniqueAppIds = Array.from(new Set(appIds));
    const entries = await Promise.all(
      uniqueAppIds.map(async (appId) => {
        try {
          const seatInfo = await api.seats.getAvailable(id, appId);
          return [appId, seatInfo] as const;
        } catch {
          return [appId, null] as const;
        }
      })
    );

    const nextMap: Record<string, SeatInfo> = {};
    entries.forEach(([appId, seatInfo]) => {
      if (seatInfo) {
        nextMap[appId] = seatInfo;
      }
    });
    setSeatInfoByApp(nextMap);
  };

  const handleInvite = async (values: { email: string }) => {
    try {
      setInviting(true);
      await api.invitations.create(id!, values.email);
      message.success('Invitation sent successfully (user will join as MEMBER)');
      setInviteModalOpen(false);
      form.resetFields();
      loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to send invitation');
    } finally {
      setInviting(false);
    }
  };

  const handlePromoteMember = async (userId: string) => {
    try {
      await api.organizations.promoteMember(id!, userId);
      message.success('Member promoted to ADMIN');
      loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to promote member');
    }
  };

  const handleDemoteMember = async (userId: string) => {
    try {
      await api.organizations.demoteMember(id!, userId);
      message.success('Member demoted to MEMBER');
      loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to demote member');
    }
  };

  const handleRemoveMember = async (userId: string) => {
    try {
      await api.organizations.removeMember(id!, userId);
      message.success('Member removed successfully');
      loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to remove member');
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      await api.invitations.revoke(id!, inviteId);
      message.success('Invitation revoked successfully');
      loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to revoke invitation');
    }
  };

  const handleDeleteOrg = async () => {
    try {
      await api.organizations.delete(id!);
      message.success('Organization deleted successfully');
      navigate('/organizations');
    } catch (error: any) {
      message.error(error.message || 'Failed to delete organization');
    }
  };

  if (loading) {
    return (
        <Card loading />
    );
  }

  const isOwner = org?.role === 'OWNER';
  const isAdmin = org?.role === 'ADMIN' || isOwner;
  const subscriptionApps = (org?.subscriptions || [])
    .map((sub: any) => sub.app)
    .filter(Boolean);

  return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>{org?.name}</Title>
            <Tag color={org?.role === 'OWNER' ? 'gold' : org?.role === 'ADMIN' ? 'blue' : 'default'}>
              {org?.role}
            </Tag>
          </div>
          {isOwner && (
            <Popconfirm
              title="Delete organization?"
              description="This action cannot be undone. All data will be lost."
              onConfirm={handleDeleteOrg}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />}>
                Delete Organization
              </Button>
            </Popconfirm>
          )}
        </div>

        <Card title="Organization Details">
          <Descriptions column={1} bordered>
            <Descriptions.Item label="Name">{org?.name}</Descriptions.Item>
            <Descriptions.Item label="Slug">{org?.slug}</Descriptions.Item>
            <Descriptions.Item label="Description">
              {org?.description || 'No description'}
            </Descriptions.Item>
            <Descriptions.Item label="Created">
              {new Date(org?.createdAt).toLocaleDateString()}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          title="Members"
          extra={
            isAdmin && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setInviteModalOpen(true)}
              >
                Invite Member
              </Button>
            )
          }
        >
          <Tabs
            items={[
              {
                key: 'members',
                label: `Members (${members.length})`,
                children: (
                  <List
                    dataSource={members}
                    renderItem={(member) => {
                      const actions = [];

                      // Only admins/owners can manage members (except owners)
                      if (isAdmin && member.role !== 'OWNER') {
                        // Add promote/demote button
                        if (member.role === 'MEMBER') {
                          actions.push(
                            <Button
                              type="link"
                              onClick={() => handlePromoteMember(member.userId)}
                            >
                              Promote to Admin
                            </Button>
                          );
                        } else if (member.role === 'ADMIN') {
                          actions.push(
                            <Button
                              type="link"
                              onClick={() => handleDemoteMember(member.userId)}
                            >
                              Demote to Member
                            </Button>
                          );
                        }

                        // Add remove button
                        actions.push(
                          <Popconfirm
                            title="Remove member?"
                            onConfirm={() => handleRemoveMember(member.userId)}
                            okText="Remove"
                            okButtonProps={{ danger: true }}
                          >
                            <Button type="link" danger>
                              Remove
                            </Button>
                          </Popconfirm>
                        );
                      }

                      return (
                        <List.Item actions={actions}>
                          <List.Item.Meta
                            avatar={<UserOutlined style={{ fontSize: 24 }} />}
                            title={member.user.name || member.user.email}
                            description={member.user.email}
                          />
                          <Tag color={member.role === 'OWNER' ? 'gold' : member.role === 'ADMIN' ? 'blue' : 'default'}>
                            {member.role}
                          </Tag>
                        </List.Item>
                      );
                    }}
                  />
                ),
              },
              ...(isAdmin
                ? [{
                    key: 'invitations',
                    label: `Pending Invitations (${invitations.filter(i => i.status === 'PENDING').length})`,
                    children: (
                      <List
                        dataSource={invitations.filter(i => i.status === 'PENDING')}
                        renderItem={(invite) => (
                          <List.Item
                            actions={[
                              <Button
                                type="link"
                                danger
                                onClick={() => handleRevokeInvite(invite.id)}
                              >
                                Revoke
                              </Button>,
                            ]}
                          >
                            <List.Item.Meta
                              avatar={<MailOutlined style={{ fontSize: 24 }} />}
                              title={invite.email}
                              description={`Expires ${new Date(invite.expiresAt).toLocaleDateString()}`}
                            />
                            <Tag color="default">MEMBER</Tag>
                          </List.Item>
                        )}
                      />
                    ),
                  }]
                : []),
            ]}
          />
        </Card>

        <Card
          title="Seat Assignments"
          extra={
            isAdmin && subscriptionApps.length > 0 ? (
              <Form
                form={assignForm}
                layout="inline"
                onFinish={async (values) => {
                  try {
                    setAssigning(true);
                    await api.seats.assign(id!, values.userId, values.appId);
                    message.success('Seat assigned');
                    assignForm.resetFields();
                    await loadData();
                  } catch (error: any) {
                    message.error(error.message || 'Failed to assign seat');
                  } finally {
                    setAssigning(false);
                  }
                }}
              >
                <Form.Item
                  name="userId"
                  rules={[{ required: true, message: 'Select a member' }]}
                >
                  <Select
                    placeholder="Select member"
                    style={{ width: 200 }}
                    options={members.map((member) => ({
                      value: member.userId,
                      label: member.user.name || member.user.email,
                    }))}
                  />
                </Form.Item>
                <Form.Item
                  name="appId"
                  rules={[{ required: true, message: 'Select an app' }]}
                >
                  <Select
                    placeholder="Select app"
                    style={{ width: 200 }}
                    options={subscriptionApps.map((app: any) => ({
                      value: app.id,
                      label: app.name,
                    }))}
                  />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" loading={assigning}>
                    Assign Seat
                  </Button>
                </Form.Item>
              </Form>
            ) : null
          }
        >
          {subscriptionApps.length === 0 ? (
            <Text type="secondary">No active subscriptions available for seat assignment.</Text>
          ) : (
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <Space wrap>
                {subscriptionApps.map((app: any) => {
                  const seatInfo = seatInfoByApp[app.id];
                  const label = seatInfo
                    ? `${seatInfo.usedSeats}/${seatInfo.totalSeats} seats`
                    : 'Seats: —';
                  return (
                    <Tag key={app.id} color="blue">
                      {app.name}: {label}
                    </Tag>
                  );
                })}
              </Space>

              <List
                dataSource={seatAssignments}
                locale={{ emptyText: 'No seat assignments yet' }}
                renderItem={(assignment) => (
                  <List.Item
                    actions={
                      isAdmin
                        ? [
                            <Popconfirm
                              title="Remove seat assignment?"
                              onConfirm={async () => {
                                try {
                                  await api.seats.unassign(id!, assignment.userId, assignment.appId);
                                  message.success('Seat unassigned');
                                  await loadData();
                                } catch (error: any) {
                                  message.error(error.message || 'Failed to unassign seat');
                                }
                              }}
                              okText="Remove"
                              okButtonProps={{ danger: true }}
                            >
                              <Button type="link" danger>
                                Unassign
                              </Button>
                            </Popconfirm>,
                          ]
                        : []
                    }
                  >
                    <List.Item.Meta
                      title={`${assignment.user.name || assignment.user.email}`}
                      description={`${assignment.user.email} • ${assignment.app.name}`}
                    />
                    <Tag color="default">{assignment.app.slug}</Tag>
                  </List.Item>
                )}
              />
            </Space>
          )}
        </Card>

        <Modal
          title="Invite Member"
          open={inviteModalOpen}
          onCancel={() => {
            setInviteModalOpen(false);
            form.resetFields();
          }}
          footer={null}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleInvite}
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter a valid email' },
              ]}
            >
              <Input placeholder="user@example.com" />
            </Form.Item>

            <div style={{
              padding: '12px',
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: '6px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <InfoCircleOutlined style={{ color: '#1890ff', marginTop: '2px' }} />
              <div style={{ flex: 1, fontSize: '14px', color: '#0c1d2f' }}>
                Invited users will join as <strong>MEMBER</strong>. You can promote them to <strong>ADMIN</strong> later from the members list.
              </div>
            </div>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={inviting}>
                  Send Invitation
                </Button>
                <Button
                  onClick={() => {
                    setInviteModalOpen(false);
                    form.resetFields();
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Space>
  );
}
